<template>
  <div>
    <field-panel
      :title="title"
      :info-popup-text="trans('infoText.diagnosisAndTreatment')"
    >
      <div class="panel-body">
        <clip-loader v-show="loading" />
        <div v-if="examinationData.length > 0" class="row align-items-end">
          <column v-for="(field, index) in examinationData" :key="index">
            <field
              :examination-id="examination.id"
              :title="field.title"
              :data="field.data"
              :type="field.type"
              :disabled="field.disabled"
              :form-value="field.formValue"
              :extra-options="field.extraOptions"
            />
          </column>
        </div>
      </div>
    </field-panel>

    <field-panel
      class="mt-3"
      :title="documentationTitle"
      :info-popup-text="getInfoPopupText()"
      :new-documentation-button="
        !addNewDocumentation
          ? {
              onAddNewDocumentation: onAddNewDocumentation,
            }
          : null
      "
      :total-comments="totalComments"
    >
      <examination-documentation
        v-if="examinationData.length"
        :examination-id="
          examination.main_examination_id
            ? examination.main_examination_id
            : examination.id
        "
        :enabled-types="enabledTypes"
        :add-new-documentation="addNewDocumentation"
        @dismiss-add-new-documentation="dismissAddNewDocumentation"
        @set-total-comments="setTotalComments"
      />
    </field-panel>
  </div>
</template>

<script>
import Column from "./components/Column.vue";
import SubColumn from "./components/SubColumn.vue";
import FieldPanel from "./components/FieldPanel.vue";
import ExaminationDocumentation from "../../../Examinations/Examination/ExaminationComponents/ExaminationDocumentation.vue";
import Notifications from "../../../Utils/notifications";

import { FIELD_CONSTANTS } from "../fieldMap";
import {
  getEnabledDocumentationTypes,
  getFieldType,
} from "../../../../helpers/product";
import { STORE_CONSTANTS } from "../../../../helpers/definitions";
import { infoPopupText } from "../../../../helpers/misc";
import {
  getFieldConditionsByOperator,
  getFieldConditionsByType,
  isDisabled,
  isVisible,
} from "../../../../lib/field-conditions";
import { inject } from "vue";

export default {
  components: {
    Column,
    SubColumn,
    FieldPanel,
    ExaminationDocumentation,
  },
  props: {
    fields: Array,
    examination: Object,
    fieldGroupName: String,
  },
  setup() {
    const reloadMainExamination = inject("reloadMainExamination", null);
    return { reloadMainExamination };
  },
  data() {
    return {
      examinationData: [],
      FIELD_CONSTANTS: FIELD_CONSTANTS,
      title: "examination.diagnosisAndTreatmentHeadline",
      documentationTitle: "examination.consultBoxLabel",
      enabledTypes: [],
      loading: false,
      addNewDocumentation: false,
      totalComments: null,
      localValues: {},
    };
  },
  watch: {
    examination() {
      this.initLocalValues();
      this.mapData();
    },
  },
  mounted: function () {
    this.initLocalValues();
    this.mapData();
    const product = this.$store.state.products[this.examination.type.id];
    this.enabledTypes = getEnabledDocumentationTypes(product);
  },
  methods: {
    initLocalValues: function () {
      const { fields, examination } = this;
      const values = {};
      if (fields && examination?.diagnosis_form) {
        fields.forEach((field) => {
          if (field.name in examination.diagnosis_form) {
            values[field.name] = examination.diagnosis_form[field.name];
          }
        });
      }
      this.localValues = values;
    },
    mapData: function () {
      const { fields, examination } = this;
      const examinationData = [];
      fields.forEach((field) => {
        const valueOptions = JSON.parse(
          JSON.stringify(JSON.parse(field.value_options)),
        );

        /**
         * Apply field conditions logic (same as EditExamination.vue)
         * Only use conditions of type "equals", they are the only ones relevant for the form
         */
        const filteredConditions = getFieldConditionsByOperator(
          field.field_conditions ?? [],
          "equals",
        );
        const visibleConditions = getFieldConditionsByType(
          filteredConditions,
          "visible",
        );
        const disabledConditions = getFieldConditionsByType(
          filteredConditions,
          "disabled",
        );
        const values = {
          ...examination,
          ...examination?.diagnosis_form,
          ...this.localValues,
        };

        const fieldConditions = {
          visible: isVisible(visibleConditions, values),
          disabled: isDisabled(disabledConditions, values),
        };

        if (field.visible && fieldConditions.visible) {
          examinationData.push({
            title: field.name,
            data: valueOptions
              ? valueOptions.map((option) => ({
                  label: this.trans(`examination.${option.title}`),
                  value: option.data,
                }))
              : "",
            type: getFieldType(field),
            disabled: field.disabled || fieldConditions.disabled,
            formValue: this.getFormValue(
              this.localValues[field.name] !== undefined
                ? this.localValues[field.name]
                : examination?.diagnosis_form?.[field.name],
            ),
            extraOptions: {
              onValueChange: this.onValueChange,
              key: field.name,
              postUrl: `/api/internal/tumour-wound/save-diagnostic/${examination.id}`,
            },
          });
        }
      });
      this.examinationData = examinationData;
    },
    getFormValue: function (formValue) {
      if (!formValue) return;
      const hasMultipleValues =
        typeof formValue === "object" && formValue.constructor === Object;
      if (hasMultipleValues && formValue) {
        const result = [];
        Object.keys(formValue).forEach((val) => {
          if (formValue[val]) {
            result.push({
              label: this.trans(`examination.${val}`),
              value: val,
            });
          }
        });
        return result;
      }
      return {
        label: this.trans(`examination.${formValue}`),
        value: formValue,
      };
    },
    onValueChange: async function (input) {
      const isBatch = Array.isArray(input.value);
      const backupValue = this.localValues[input.key];

      // Update local values so field conditions react immediately
      this.localValues[input.key] = isBatch
        ? input.value.map((val) => val.value)
        : input.value;
      this.mapData();

      const success = await this.postValues({
        resources: isBatch
          ? JSON.stringify(input.value.map((val) => val.value))
          : input.value,
        key: input.key,
      });
      if (success) {
        Notifications.saveSuccess();
        if (this.reloadMainExamination) {
          this.reloadMainExamination();
        }
        this.$store.dispatch(STORE_CONSTANTS.LOAD_PATIENT_CASES);
      } else {
        this.localValues[input.key] = backupValue;
        this.mapData();
      }
    },
    postValues: async function (formData) {
      const postUrl = `/api/internal/tumour-wound/save-diagnostic/${this.examination.id}`;
      this.loading = true;
      try {
        const result = await axios.post(postUrl, formData);
        this.loading = false;
        return true;
      } catch (e) {
        this.loading = false;
        this.userErrorMessage(e);
        return false;
      }
    },
    onAddNewDocumentation: function () {
      this.addNewDocumentation = true;
    },
    dismissAddNewDocumentation: function (bool) {
      this.addNewDocumentation = bool;
    },
    setTotalComments: function (num) {
      this.totalComments = num;
    },
    getInfoPopupText: function () {
      return this.enabledTypes.length > 0
        ? infoPopupText(this.enabledTypes, this.fieldGroupName)
        : "";
    },
  },
};
</script>
